import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const PRIVATE_ROUTE_PREFIXES = [
  '/admin',
  '/dashboard',
  '/planner',
  '/auth',
  '/client/auth',
  '/vendor/auth',
  '/login',
  '/register',
  '/reset-password',
  '/vendor/reset-password',
  '/vendor/dashboard',
  '/vendor/pricing',
];

const DEFAULT_PUBLIC_META = {
  '/': {
    title: 'Gibraltar Wedding Expo - Find Wedding Vendors in Gibraltar',
    description:
      "Discover Gibraltar's finest wedding vendors. Browse photographers, venues, caterers, florists, and more.",
  },
  '/vendors': {
    title: 'Wedding Vendors in Gibraltar | Gibraltar Wedding Expo',
    description:
      'Browse trusted wedding vendors in Gibraltar. Compare suppliers, view profiles, and plan your day with confidence.',
  },
  '/blog': {
    title: 'Gibraltar Wedding Blog | Planning Advice & Real Tips',
    description:
      'Read Gibraltar wedding planning advice, supplier tips, and destination wedding inspiration from Gibraltar Wedding Expo.',
  },
  '/getting-married-in-gibraltar': {
    title: 'Getting Married in Gibraltar | Complete Wedding Guide',
    description:
      'Planning on getting married in Gibraltar? Discover how Gibraltar weddings work, what documents you may need, where to marry, and which local wedding suppliers can help.',
  },
  '/how-to-get-married-in-gibraltar': {
    title: 'How to Get Married in Gibraltar | Step-by-Step Guide',
    description:
      'Learn how to get married in Gibraltar, including the basic process, documents, ceremony options, overnight stay guidance and local wedding suppliers to book.',
    canonical: 'https://www.gibraltarweddingexpo.online/how-to-get-married-in-gibraltar',
  },
  '/cost-of-getting-married-in-gibraltar': {
    title: 'Cost of Getting Married in Gibraltar | Wedding Budget Guide',
    description:
      'Find out what can affect the cost of getting married in Gibraltar, including official marriage fees, venues, suppliers, packages, receptions and wedding budget tips.',
    canonical: 'https://www.gibraltarweddingexpo.online/cost-of-getting-married-in-gibraltar',
  },
  '/civil-registry-weddings-gibraltar': {
    title: 'Civil & Registry Weddings in Gibraltar | Registry Office Guide',
    description:
      'Planning a civil or registry office wedding in Gibraltar? Learn about civil ceremonies, the Registrar, documents, approved venues, witnesses and local suppliers.',
    canonical: 'https://www.gibraltarweddingexpo.online/civil-registry-weddings-gibraltar',
  },
  '/elope-in-gibraltar': {
    title: 'Elope in Gibraltar | Elopement Packages & Planning',
    description:
      'Plan your Gibraltar elopement with practical legal guidance, package options, and trusted local suppliers.',
  },
  '/about-gibraltar': {
    title: 'About Gibraltar | Destination Wedding Location Guide',
    description:
      'Learn why Gibraltar is a leading destination wedding location, from legal simplicity to iconic scenery and year-round sun.',
  },
  '/about-expo': {
    title: 'About Gibraltar Wedding Expo | Wedding Vendor Directory',
    description:
      'Discover how Gibraltar Wedding Expo helps couples find trusted wedding vendors and plan destination weddings with confidence.',
  },
  '/free-guide': {
    title: 'Free Gibraltar Wedding Guide | Gibraltar Wedding Expo',
    description:
      'Download your free Gibraltar wedding planning guide and get practical insights on legal steps, suppliers, and timelines.',
  },
};

const upsertMeta = (key, value, attribute = 'name') => {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector(`meta[${attribute}='${key}']`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
};

const upsertCanonical = (href) => {
  if (typeof document === 'undefined') return;
  let link = document.querySelector("link[rel='canonical']");
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

export const RouteSeoManager = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const { pathname, search } = location;
    const origin = window.location.origin;
    const params = new URLSearchParams(search);

    const isPrivateRoute = PRIVATE_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
    const isVendorsQueryRoute = pathname === '/vendors' && (params.get('search') || params.get('category'));

    const routeDefaults = DEFAULT_PUBLIC_META[pathname];
    if (routeDefaults) {
      document.title = routeDefaults.title;
      upsertMeta('description', routeDefaults.description, 'name');
    }

    const robotsValue = isPrivateRoute
      ? 'noindex, nofollow'
      : isVendorsQueryRoute
        ? 'noindex, follow'
        : 'index, follow';

    upsertMeta('robots', robotsValue, 'name');
    upsertCanonical(routeDefaults?.canonical || `${origin}${pathname}`);

    const syncSocialTags = () => {
      const title = document.title || 'Gibraltar Wedding Expo';
      const description =
        document.querySelector("meta[name='description']")?.getAttribute('content')
        || DEFAULT_PUBLIC_META['/'].description;

      upsertMeta('og:title', title, 'property');
      upsertMeta('og:description', description, 'property');
      upsertMeta('og:url', `${origin}${pathname}`, 'property');
      upsertMeta('og:type', pathname.startsWith('/blog/') ? 'article' : 'website', 'property');

      upsertMeta('twitter:card', 'summary_large_image', 'name');
      upsertMeta('twitter:title', title, 'name');
      upsertMeta('twitter:description', description, 'name');
      upsertMeta('twitter:url', `${origin}${pathname}`, 'name');
    };

    syncSocialTags();
    const delayedSync = window.setTimeout(syncSocialTags, 1200);

    return () => window.clearTimeout(delayedSync);
  }, [location]);

  return null;
};
